import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom'; // Added Link for Signup navigation
import { AuthContext } from '../context/AuthContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { LogIn, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import API from '../services/api';
import AuthLayout from '../component/ui/AuthLayout';
import { inputClass } from '../component/ui/Field';
import Button from '../component/ui/Button';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false); // Loading state for handling loader
  const { user, login } = useContext(AuthContext);
  const navigate = useNavigate();


  useEffect(() => {
    // Redirect to dashboard if user is already logged in
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // Start loading
   
    try {
     const response = await API.post(`${process.env.REACT_APP_API_URL}/api/login`, { email, password });
      login(response.data.token);
      navigate('/dashboard'); // Land on the stats overview after login
    } catch (error) {
      toast.error(`${error.response.data.message}, Email Or Password Not Match`, {
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick:true,
        pauseOnHover:true,
        draggable:true,
        progress:undefined,
        theme:'dark'
      });
      console.error('Login failed', error);
    } finally {
      setLoading(false); // Stop loading
    }
  };
  

  return (
    <AuthLayout>
      <div className="flex items-center gap-2.5 mb-1">
        <span className="h-9 w-9 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center">
          <LogIn size={18} />
        </span>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Sign in</h1>
          <p className="text-xs text-slate-500">Access your Landschaft workspace</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Email</label>
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`${inputClass} pl-9`}
              required
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-medium text-slate-600">Password</label>
            <Link to="/forgot-password" className="text-xs font-medium text-blue-700 hover:text-blue-800 hover:underline">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`${inputClass} pl-9 pr-9`}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <Button type="submit" variant="accent" size="lg" className="w-full" disabled={loading}>
          {loading ? (
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
          ) : 'Sign in'}
        </Button>

        <p className="text-center text-sm text-slate-500 pt-1">
          Don&apos;t have an account?{' '}
          <Link to="/signup" className="text-blue-700 font-medium hover:underline">
            Create one
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
};

export default Login;
